import { ReactElement, useEffect, useState } from "react";
import {
  FormControl,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Autocomplete
} from "@mui/material";
import StyledTableRow from "../../../components/styledTableRow";
import {
  IAcquisitionVariable,
  IDashboardAcquisitionVariable,
  IDashboardVariable
} from "../types";
import { Add, Delete } from "@mui/icons-material";
import { getPalette, getPaletteColor } from "../../../configuration/configuration";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { NullValue } from "../../../types";
import { getUID } from "../../../system/utils";
import VariablesConfigDialog from "./variablesConfigDialog";
import { useLazyFetch } from "../../../system/useFetch";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TableContainer from "../../../components/tableContainer";
import ColorPicker from "../../../components/colorPicker";

interface IProps {
  variables: IDashboardAcquisitionVariable[];
  updateVariables: (variables: IDashboardAcquisitionVariable[]) => void;
  machine: string | null;
  indicatorMode: boolean;
}

interface IDialogContext {
  open: boolean;
  selectedVariable: IDashboardAcquisitionVariable | null;
  metaData: IAcquisitionVariable | null;
}

const maxVar = 15;

const VariablesTable = ({ variables, updateVariables, machine, indicatorMode }: IProps): ReactElement => {
  const [ dialogContext, setDialogContext ] = useState<IDialogContext>({ open: false, selectedVariable: null, metaData: null });
  const [ getMachineVariables, { data } ] = useLazyFetch<IAcquisitionVariable[]>();
  const [ machineVariables, setMachineVariables ] = useState<IAcquisitionVariable[]>([]);
  const [ colors, setColors ] = useState<string[]>( variables.map((v: IDashboardAcquisitionVariable) => v.color));

  useEffect(() => {
    if (machine) {
      getMachineVariables({
        module: "acquisition",
        query: "getMachineVariables",
        payload: {
          machine
        }
      });
    } else {
      setMachineVariables([]);
    }
  }, [ machine ]);

  useEffect(() => {
    setMachineVariables((data ?? []).sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }

      return b.name < a.name ? 1 : 0;
    }));

    if (data) {

      variables.forEach(v => {
        const found = data.find(x => x.code === v.field);
        v.title = found ? found.name :  v.field;
      });

      updateVariables([...variables]);
    }

  }, [ data ]);

  const addSeries = () => {
    const color = getPaletteColor();
    setColors([ ...colors, color]);

    updateVariables([
      ...variables,
      {
        hash: getUID(),
        field: "",
        title: "",
        color: color,
        unit: "",
        decimals: 0,
        limits: [],
        annotations: [],
        yAxis: {
          name: "Y",
          minimum: 0,
          maximum: 100,
          ticksCount: 3
        }
      } ]);
  };

  const removeSeries = (index: number) => {
    const newArray = [ ...variables ];
    newArray.splice(index, 1);
    updateVariables(newArray);

    const newColors = [ ...colors];
    newColors.splice(index, 1);
    setColors(newColors);
  };

  const updateItemsSeries = (index: number, field: keyof IDashboardVariable, value: any) => {
    const newArray = [ ...variables ];
    const item = newArray[index] as any;
    if (field === "field") {
      item[field] = value;
      const found = machineVariables.find(x => x.code === value);
      if (found) {
        item.title = found.name;
        item.unit = found.axisUnit ?? "";
        item.yAxis =  {
          name: found.axisName,
          minimum: found.axisMinimum,
          maximum: found.axisMaximum,
          ticksCount: 3
        };
      }

    } else if (field === "color"){
      const newColors = [ ...colors];
      newColors[index] = value;
      setColors(newColors);
      item[field] = newColors[index];
    } else {
      item[field] = value;
    }

    updateVariables(newArray);
  };

  return (
    <TableContainer sx={{ maxHeight: 570 }}>
      <Table stickyHeader={true}  size={"small"}>
        <TableHead>
          <TableRow>
            <TableCell className="table-header" style={{ maxWidth: 24, width: 24, padding: 4 }}>
              <IconButton sx={{ padding: 0, margin: 0 }} style={{ maxWidth: 21 }} onClick={addSeries} disabled={variables.length >= maxVar}>
                <Tooltip title={"Add variable"}>
                  <Add style={{ color: getPalette().statusColor.info.background }}/>
                </Tooltip>
              </IconButton>
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 180, maxWidth: 250 }}>
              Variable - {variables.length} / {maxVar}
            </TableCell>
            <TableCell className="table-header" style={{ minWidth: 100 }}>Unit</TableCell>
            <TableCell className="table-header" style={{ minWidth: 100 }}>Decimals</TableCell>
            <TableCell className="table-header" style={{ minWidth: 50, width: 50 }}>Color</TableCell>
            {!indicatorMode && <TableCell className="table-header" style={{ minWidth: 50, width: 50 }}>Config</TableCell>}
            <TableCell className="table-header" style={{ minWidth: 30, width: 30 }}/>
          </TableRow>
        </TableHead>
        <TableBody>
          {variables.map((row, index) => {
            let units: string[] = [ "" ];
            const found = machineVariables.find(x => x.code === row.field) ?? null;
            if (found && found.axisUnit) {
              units = [ found.axisUnit ];
            }

            if(row.unit && !units.find(u => u === row.unit)){
              units.push(row.unit);
            }

            return (
              <StyledTableRow hover={true} role="checkbox" tabIndex={-1} key={`series-row-${index}`}>
                <TableCell  style={{ maxWidth: 24, width: 24, padding: 4, textAlign: "right" }}>
                  {index + 1}
                </TableCell>
                <TableCell style={{ minWidth: 180, maxWidth: 250 }}>
                  {/*<Select*/}
                  {/*  className="w-100"*/}
                  {/*  value={found ? row.field != "" ? row.field : NullValue : NullValue}*/}
                  {/*  displayEmpty={true}*/}
                  {/*  onChange={(event: ChangeEvent<{ value: unknown, }>) => updateItemsSeries(index, "field", event.target.value as string)}>*/}
                  {/*  {machineVariables.map((field, index2) => <MenuItem key={`field-${index}-${index2}`} value={field.code}>{field.name}</MenuItem>)}*/}
                  {/*  <MenuItem value={NullValue}/>*/}
                  {/*</Select>*/}
                  <Autocomplete
                    options={machineVariables}
                    getOptionLabel={(option: IAcquisitionVariable) => option.name}
                    value={found}
                    openOnFocus={true}
                    onChange={(_event: any, newValue: IAcquisitionVariable | null) => {
                      updateItemsSeries(index, "field", newValue?.code ?? NullValue);
                    }}
                    renderInput={(params) =>
                      <TextField {...params} variant="standard" color={"primary"}/>
                    }
                  />
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    <Select
                      variant="standard"
                      value={row.unit ?? ""}
                      displayEmpty={true}
                      onChange={(event: SelectChangeEvent) => updateItemsSeries(index, "unit", event.target.value)}>
                      { units.map((unit, uIndex) => {
                        return <MenuItem key={`unit-${index}-${uIndex}`} value={unit}>{unit}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    <TextField
                      variant="standard"
                      size="small"
                      value={row.decimals}
                      type="number"
                      slotProps={{ input: {  inputProps: { min: 0, max: 6 } }}}
                      onChange={(event) => updateItemsSeries(index, "decimals",  parseInt(event.target.value as string, 10))}
                    />
                  </FormControl>
                </TableCell>

                <TableCell>
                  <FormControl className="w-100">
                    <ColorPicker color={colors[index]} onChange={(color) => updateItemsSeries(index, "color", color)} disableAlpha={true}/>
                  </FormControl>
                </TableCell>

                {!indicatorMode && <TableCell>
                  <IconButton sx={{ padding: 0, margin: 0 }}
                    disabled={row.field === ""}
                    onClick={() => setDialogContext({ open: true, selectedVariable: row, metaData: found })}>
                    <Tooltip title={"Config"}>
                      <MoreVertIcon style={{ color: getPalette().statusColor.info.background }}/>
                    </Tooltip>
                  </IconButton>
                </TableCell>}

                <TableCell>
                  <IconButton sx={{ padding: 0, margin: 0 }} onClick={() => removeSeries(index)} disabled={variables.length === 1}>
                    <Tooltip title={"Remove variable"}>
                      <Delete style={{ color: getPalette().statusColor.error.background }}/>
                    </Tooltip>
                  </IconButton>
                </TableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
      <VariablesConfigDialog
        machineVariables={machineVariables}
        variable={dialogContext.selectedVariable}
        metaData={dialogContext.metaData}
        open={dialogContext.open}
        setOpen={() => setDialogContext({ open: false, selectedVariable: null, metaData: null })} />
    </TableContainer>
  );
};

export default VariablesTable;

